import { FC, useEffect, useState } from 'react';
import { AnalyticsViewProps } from './analytics.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  getAnalyticsViewTabMenuItems,
  getAnalyticsTabIndex,
  getAnalyticsSubRouteId,
  analyticsSubRouteIds,
} from './analytics.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { PermissionGate } from '../../../components/rbac/PermissionGate';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';

// Create a separate component for the content that needs access to navigation
const AnalyticsContent: FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { analyticsSubRouteId } = useParams();
  const navigate = useNavigate();
  const { translateText } = useTranslation();
  const safeLanding = useRbacSafeLandingPath();

  useEffect(() => {
    setSelectedTab(
      getAnalyticsTabIndex(
        analyticsSubRouteId && analyticsSubRouteId !== 'undefined'
          ? analyticsSubRouteId
          : analyticsSubRouteIds[0]
      )
    );
    navigate(
      pages.analytics.path +
      '/' +
      `${analyticsSubRouteId && analyticsSubRouteId !== 'undefined'
        ? analyticsSubRouteId
        : analyticsSubRouteIds[0]
      }`,
      {
        replace: true,
      }
    );
  }, [analyticsSubRouteId, navigate]);

  const locationChangeHandler = (value: string | number) => {
    setSelectedTab(value as number);
    navigate(
      pages.analytics.path + '/' + getAnalyticsSubRouteId(value as number),
      {
        replace: true,
      }
    );
  };

  return (
    <PermissionGate
      module="analytics"
      submodule="performance"
      permission="any"
      fallback={<Navigate to={safeLanding} replace />}
    >
      <Grid
        container
        sx={{
          height: '100%',
          px: { xs: 0, sm: 0 },
        }}
        data-testid="analytics-view-container"
      >
        <Grid
          item
          xs={12}
          sx={{
            pr: { xs: 0, sm: 4 },
          }}
          data-testid="analytics-view-header-container"
        >
          <MainTitle title={translateText('analytics.title')}
            subTitle={selectedTab ? getAnalyticsViewTabMenuItems(translateText)[selectedTab].label : ''}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: 'calc(100% - 64px)',
            px: { xs: 0, sm: 0 },
          }}
          data-testid="analytics-view-tabs-container"
        >
          <TabGroup
            key={selectedTab}
            tabs={getAnalyticsViewTabMenuItems(translateText)}
            initiallySelected={selectedTab}
            onTabChange={(value) => locationChangeHandler(value)}
            dataTestIdPrefix="analytics"
          />
        </Grid>
      </Grid>
    </PermissionGate>
  );
};

// Main component that provides navigation context
const AnalyticsView: FC<AnalyticsViewProps> = () => {
  return (
    <NavigationDrawer
      selectedPage={pages.analytics.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.analytics}</title>
      </Helmet>
      <AnalyticsContent />
    </NavigationDrawer>
  );
};

export default AnalyticsView;
